import { useDispatch, useSelector } from "react-redux";
import {
  createNewTranslation,
  DEFAULT_LETTER_TEMPLATE_VALUES,
  getLanguageLabel,
  LETTER_TEMPLATE_DEVICE_TYPES,
  LETTER_TEMPLATE_LANGUAGES,
} from "../letterTemplateTypes";
import { useState } from "react";
import {
  useCreateLetterTemplateMutation,
  useUpdateLetterTemplateMutation,
} from "app/store/letterTemplate/api/letterTemplateApi";
import { createFilterOptions } from "@mui/material";
import { letterTemplateValidationSchema } from "schemas/letterTemplateValidationSchema";
import { showErrorToast, showSuccessToast } from "utils/toast";
import { patientProfileDocumentApi } from "app/store/patientProfile/documents/api/patientProfileDocumentApi";
import { useGetDeviceListQuery } from "app/store/devices/deviceApi";
import useCheckSaasAccountUser from "hooks/useCheckSaasAccountUser";
import { evokeManufacturers, manufacturers } from "static/manufacturers";

const useLetterTemplateForm = ({
  active,
  isUpdate,
  handleClose,
  selectedTemplate,
}) => {
  const { data: deviceModels } = useGetDeviceListQuery();
  const deviceModelOptions =
    deviceModels
      ?.filter((d) => d?.modelDisplayName)
      .map((d) => d.modelDisplayName) || [];

  const isSaasAccountUser = useCheckSaasAccountUser();
  // Excludes patch manufacturers.
  const manufacturerList = isSaasAccountUser
    ? manufacturers
    : [...manufacturers, ...evokeManufacturers];

  const mapTemplateTranslations = (template) => {
    const source =
      template?.translated_templates ?? template?.translations ?? [];

    if (Array.isArray(source)) {
      return source.map((t, index) => ({
        id: t?.id || `translation_${index}_${Date.now()}`,
        language: (t?.language || t?.lang || "").toLowerCase(),
        content: t?.content || t?.template || t?.body || "",
      }));
    }

    // Object map e.g. { es: "<html>", fr: "<html>" }
    if (source && typeof source === "object") {
      return Object.entries(source).map(([language, content], index) => ({
        id: `translation_${index}_${Date.now()}`,
        language: language.toLowerCase(),
        content: typeof content === "string" ? content : content?.content || "",
      }));
    }

    return [];
  };

  const buildInitialValues = (selectedTemplate) => {
    if (!selectedTemplate) return DEFAULT_LETTER_TEMPLATE_VALUES;

    return {
      templateName: selectedTemplate?.template_name || "",
      accounts: Array.isArray(selectedTemplate?.accounts)
        ? selectedTemplate.accounts
        : [],
      deviceModels: Array.isArray(selectedTemplate?.device_models)
        ? selectedTemplate.device_models
        : [],
      manufacturerArr: Array.isArray(selectedTemplate?.manufacturers)
        ? selectedTemplate.manufacturers
        : [],
      showBattery: selectedTemplate?.show_battery || false,
      customNextSend: selectedTemplate?.custom_next_send || false,
      associatedDevices: selectedTemplate?.associated_device
        ? selectedTemplate.associated_device
        : "",
      missingTransmissions: {
        active: selectedTemplate?.missing_transmissions?.active || false,
        transmissionCycle:
          selectedTemplate?.missing_transmissions?.transmission_cycle || "",
        useSavedNextSendCycle:
          selectedTemplate?.missing_transmissions?.use_saved_next_send_cycle ||
          false,
      },
      primaryTemplate: selectedTemplate?.primary_template || "",
      translations: mapTemplateTranslations(selectedTemplate),
    };
  };

  const { accounts } = useSelector((state) => state.account);
  const dispatch = useDispatch();

  const [submitClicked, setSubmitClicked] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedLanguageTab, setSelectedLanguageTab] = useState("primary");
  const [isHtmlEditor, setIsHtmlEditor] = useState(true);
  const [isAddingTranslation, setIsAddingTranslation] = useState(false);

  const [createLetterTemplate] = useCreateLetterTemplateMutation();
  const [updateLetterTemplate] = useUpdateLetterTemplateMutation();

  const filter = createFilterOptions();
  // Form state
  const [values, setValues] = useState(() =>
    buildInitialValues(selectedTemplate)
  );

  // Map accounts for select options
  const accountOptions = accounts?.map((account) => account.name) || [];

  // Map device types for select options
  const deviceTypeOptions =
    LETTER_TEMPLATE_DEVICE_TYPES?.map((device) => device.label) || [];

  const setFieldValue = (field, value) => {
    if (field.includes(".")) {
      const [parent, child] = field.split(".");
      setValues((prev) => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value,
        },
      }));
    } else {
      setValues((prev) => ({ ...prev, [field]: value }));
    }
  };

  const validateForm = async () => {
    try {
      await letterTemplateValidationSchema.validate(values, {
        abortEarly: false,
      });
      return { isValid: true, errors: {} };
    } catch (err) {
      const validationErrors = {};
      err.inner?.forEach((error) => {
        validationErrors[error.path] = error.message;
      });
      return { isValid: false, errors: validationErrors };
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitClicked(true);

    const { isValid, errors } = await validateForm();
    if (!isValid) {
      showErrorToast("Please fill in all required fields");
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = {
        active,
        templateName: values.templateName,
        accounts: values.accounts,
        deviceModels: values.deviceModels,
        manufacturers: values.manufacturerArr,
        showBattery: values.showBattery,
        customNextSend: values.customNextSend,
        associatedDevice: values.associatedDevices,
        missingTransmissions: {
          active: values.missingTransmissions.active,
          transmission_cycle: values.missingTransmissions.transmissionCycle,
          use_saved_next_send_cycle:
            values.missingTransmissions.useSavedNextSendCycle,
        },
        primaryTemplate: values.primaryTemplate,
        translatedTemplates: values.translations.map(({ id, ...rest }) => rest),
      };

      if (isUpdate) {
        await updateLetterTemplate({
          payload,
          templateId: selectedTemplate?._id,
        }).unwrap();
        showSuccessToast("Letter template updated successfully");
      } else {
        await createLetterTemplate(payload).unwrap();
        showSuccessToast("Letter template created successfully");
      }

      await dispatch(
        patientProfileDocumentApi.util.invalidateTags(["LetterTemplates"])
      );
      handleClose();
    } catch (error) {
      showErrorToast(error?.message || "An error occurred");
    } finally {
      setIsSubmitting(false);
    }
  };

  const getUsedLanguages = () =>
    values.translations.map((t) => t.language).filter(Boolean);

  const availableLanguages = LETTER_TEMPLATE_LANGUAGES.filter(
    (lang) => !getUsedLanguages().includes(lang.value)
  );

  const handleAddTranslation = (language) => {
    const langToUse = language || availableLanguages[0]?.value;
    if (!langToUse) return;
    const newTranslation = {
      ...createNewTranslation(),
      language: langToUse,
    };
    setValues((prev) => ({
      ...prev,
      translations: [...prev.translations, newTranslation],
    }));
    setSelectedLanguageTab(newTranslation.id);
    setIsAddingTranslation(false);
  };

  const handleRemoveTranslation = (id) => {
    setValues((prev) => ({
      ...prev,
      translations: prev.translations.filter((t) => t.id !== id),
    }));
    if (selectedLanguageTab === id) setSelectedLanguageTab("primary");
  };

  const getActiveContent = () => {
    if (selectedLanguageTab === "primary") return values.primaryTemplate;
    return (
      values.translations.find((t) => t.id === selectedLanguageTab)?.content ||
      ""
    );
  };

  const setActiveContent = (content) => {
    if (selectedLanguageTab === "primary") {
      setFieldValue("primaryTemplate", content);
      return;
    }
    setValues((prev) => ({
      ...prev,
      translations: prev.translations.map((t) =>
        t.id === selectedLanguageTab ? { ...t, content } : t
      ),
    }));
  };

  const activeTranslation =
    selectedLanguageTab === "primary"
      ? null
      : values.translations.find((t) => t.id === selectedLanguageTab);

  const hubItems = [
    { id: "primary", label: "English", isPrimary: true },
    ...values.translations.map((t) => ({
      id: t.id,
      label: t.language ? getLanguageLabel(t.language) : "New Translation",
      isPrimary: false,
    })),
  ];

  const isActiveContentMissing =
    submitClicked &&
    ((selectedLanguageTab === "primary" && !values.primaryTemplate) ||
      (activeTranslation && !activeTranslation.content));

  const handleCancel = () => {
    setValues(DEFAULT_LETTER_TEMPLATE_VALUES);
    handleClose();
  };

  const handlePrintPreview = () => {
    const content = getActiveContent();

    document.getElementById("print-iframe")?.remove();

    const iframe = Object.assign(document.createElement("iframe"), {
      id: "print-iframe",
      srcdoc: `<html><head><title>Letter Preview</title><style>body{font-family:Arial,sans-serif;padding:24px}</style></head><body>${content || ""}</body></html>`,
    });
    iframe.style.cssText = "position:fixed;width:0;height:0;border:none;";
    iframe.onload = () => iframe.contentWindow.print();
    document.body.appendChild(iframe);
  };

  return {
    filter,
    values,
    accounts,
    hubItems,
    setValues,
    deviceModels,
    manufacturerList,
    deviceModelOptions,
    isSubmitting,
    isHtmlEditor,
    submitClicked,
    accountOptions,
    setIsSubmitting,
    setIsHtmlEditor,
    setSubmitClicked,
    deviceTypeOptions,
    activeTranslation,
    availableLanguages,
    selectedLanguageTab,
    isAddingTranslation,
    createLetterTemplate,
    updateLetterTemplate,
    isActiveContentMissing,
    setSelectedLanguageTab,
    setIsAddingTranslation,

    dispatch,
    handleSubmit,
    setFieldValue,
    handleCancel,
    setActiveContent,
    getActiveContent,
    handlePrintPreview,
    handleAddTranslation,
    handleRemoveTranslation,
  };
};

export default useLetterTemplateForm;
